import Image from 'next/image';
import Head from 'next/head';
import Link from 'next/link';

export default function AboutUs() {
  const teamMembers = [
    {
      name: "Rahil Muhimtule",
      role: "Lead Facilitator & Strategist",
      image: "/team/Rahil.jpeg",
      bio: "With 25 years in luxury retail management, Rahil specializes in executive presence and strategic decision-making."
    },
    {
      name: "Mikki Sharma",
      role: "Head of Learning Design",
      image: "/team/Mikki.jpeg",
      bio: "Mikki architected the L.I.S.T framework, focusing on experiential learning and behavior transformation."
    },
    {
      name: "Meghana Guzdar",
      role: "Leadership Coach",
      image: "/team/Meghana.jpeg",
      bio: "Meghana focuses on Emotional Intelligence and psychological safety within high-performance teams."
    },
    {
      name: "Ayesha Shaikh",
      role: "Operations Manager",
      image: "/team/Ayesha.jpeg",
      bio: "Ayesha ensures every workshop delivery meets the highest standards of luxury and excellence."
    }
  ];

  const values = [
    { title: "Precision", desc: "We believe in targeted, intervention-style learning that solves specific leadership gaps." },
    { title: "Authenticity", desc: "Leadership starts with self-awareness. We encourage leaders to find their true voice." },
    { title: "Impact", desc: "Our metrics are measured by the tangible growth and results of your teams." }
  ];

  return (
    <>
      <Head>
        <title>Our Story | L.I.S.T Learning Pathways</title>
        <meta name="description" content="Discover the philosophy behind L.I.S.T and our mission to build influential leaders in the luxury sector." />
      </Head>

      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-brand-dark pt-20 pb-16 lg:pt-32 lg:pb-20">
          {/* Background blobs for modern feel */}
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-primary/10 rounded-full blur-3xl"></div>

          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 text-center lg:text-left lg:grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-brand-primary/20 text-white text-base font-bold rounded-full mb-4">
                Our Genesis
              </span>
              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
                Beyond <span className="text-brand-primary">Management.</span><br />
                <span className="text-brand-primary">Strategic</span> Leadership.
              </h1>
              <p className="text-lg text-gray-300 max-w-xl mx-auto lg:mx-0 leading-relaxed mb-8">
                L.I.S.T was born from a simple realization: technical skill gets you the job, but leadership is what creates a legacy.
                We build the influential leaders of tomorrow.
              </p>
              <div className="w-20 h-1 bg-brand-primary rounded-full hidden lg:block"></div>
            </div>
            <div className="mt-12 lg:mt-0 relative">
              <div className="aspect-[4/3] relative rounded-lg overflow-hidden shadow-xl border border-white/10 group">
                <img
                  src="/list-hero/4.jpg"
                  alt="Our Team"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-brand-dark/30 group-hover:bg-brand-dark/0 transition-all duration-700"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-20 lg:py-28 bg-white relative">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col items-center text-center mb-16">
              <span className="inline-block px-4 py-1.5 bg-brand-primary/10 text-brand-primary text-sm font-bold rounded-full mb-6">
                The Foundation
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-brand-dark tracking-tight">Our Story</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-16 items-start max-w-6xl mx-auto">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-brand-primary/10 rounded-full -z-10"></div>
                <p className="text-xl md:text-2xl text-brand-dark leading-relaxed font-medium italic border-l-4 border-brand-primary pl-6 py-2">
                  "We didn't want to create another generic training company. We wanted to create a leadership laboratory for the luxury sector."
                </p>
                <div className="mt-8">
                  <p className="text-gray-600 leading-relaxed text-lg">
                    L.I.S.T (Learning, Inspiring, Strategizing, Transforming) was established to bridge the gap between academic theory and the high-pressure reality of luxury retail and hospitality.
                  </p>
                </div>
              </div>
              <div className="space-y-6">
                <p className="text-gray-600 leading-relaxed text-lg">
                  Our methodology is preventative, not reactive. We identify potential leadership bottlenecks before they affect the bottom line. By focusing on four specific pillars, we ensure that every participant doesn't just learn about leadership—they become leaders.
                </p>
                <div className="h-px w-full bg-gray-100"></div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Today, L.I.S.T is recognized as a premier destination for professionals who refuse to settle for mediocrity. We are the architects of influence.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision Cards */}
        <section className="py-16 lg:py-20 bg-gray-50">
          <div className="w-full px-6 lg:px-12">
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
              <div className="bg-white p-8 lg:p-10 rounded-lg shadow-sm border border-gray-100 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Our Mission</h3>
                <p className="text-gray-500 leading-relaxed text-base">
                  To empower individuals through targeted, high-impact leadership development that transforms organizational culture and drives exceptional business results in the global luxury market.
                </p>
              </div>
              <div className="bg-brand-dark p-8 lg:p-10 rounded-lg shadow-xl text-white">
                <h3 className="text-xl font-bold mb-4 text-gray-300">Our Vision</h3>
                <p className="text-gray-300 leading-relaxed text-base">
                  To be the global benchmark for strategic leadership education, fostering a new generation of emotionally intelligent, strategically agile, and profoundly influential leaders.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach (Pillars) */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <span className="text-brand-primary font-bold text-base mb-3 block">The Methodology</span>
              <h3 className="text-3xl md:text-4xl font-extrabold text-brand-dark">Our Approach</h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {[
                { title: "Learning", tag: "Step 01", desc: "Foundation of self-awareness and core competencies.", color: "group-hover:text-indigo-600" },
                { title: "Inspiring", tag: "Step 02", desc: "Mastering the art of influence and psychological safety.", color: "group-hover:text-rose-600" },
                { title: "Strategizing", tag: "Step 03", desc: "Critical thinking, decision-making and vision alignment.", color: "group-hover:text-teal-600" },
                { title: "Transforming", tag: "Step 04", desc: "Driving high performance and lasting cultural change.", color: "group-hover:text-amber-600" }
              ].map((step, i) => (
                <div key={i} className="group p-6 bg-gray-50/50 border border-gray-100 rounded-md hover:bg-white hover:shadow-xl transition-all duration-500">
                  <span className="text-base font-black text-brand-primary mb-4 block">{step.tag}</span>
                  <h4 className={`text-lg font-bold mb-3 text-brand-dark ${step.color} transition-colors duration-300`}>{step.title}</h4>
                  <p className="text-gray-500 text-base leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section id="team" className="py-16 lg:py-20 bg-gray-50/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <span className="text-brand-primary font-bold text-base mb-3 block">The Experts</span>
              <h3 className="text-3xl md:text-4xl font-extrabold text-brand-dark">Meet The Team</h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member, index) => (
                <div key={index} className="bg-white rounded-md overflow-hidden shadow-sm group hover:shadow-lg transition-all duration-500">
                  <div className="h-80 relative overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover grayscale brightness-90 group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-brand-dark/20 group-hover:bg-brand-dark/0 transition-all duration-500"></div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-brand-dark mb-1">{member.name}</h3>
                    <p className="text-brand-primary text-base font-bold mb-3">{member.role}</p>
                    <p className="text-gray-500 text-base leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="py-16 lg:py-20 bg-brand-dark text-white overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/dark-leather.png')] opacity-10"></div>
          <div className="w-full px-6 lg:px-12 relative z-10">
            <h3 className="text-3xl md:text-5xl font-extrabold text-white mb-12 text-center">THE <span className="text-brand-primary">PAI</span> FACTOR</h3>
            <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
              {values.map((v, i) => (
                <div key={i} className="text-center lg:text-left">

                  <h4 className="text-xl font-bold mb-3 tracking-tight">{v.title}</h4>
                  <p className="text-gray-300 leading-relaxed text-base">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Impact Section (Stats) */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <span className="text-brand-primary font-bold text-base mb-3 block">Evidence</span>
              <h3 className="text-3xl md:text-4xl font-extrabold text-brand-dark">Our Impact</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { number: "250+", label: "Leaders Trained" },
                { number: "15+", label: "Luxury Brands" },
                { number: "98%", label: "Satisfaction Rate" },
                { number: "Mumbai", label: "Operations Hub" }
              ].map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="text-3xl md:text-5xl font-extrabold text-brand-dark mb-3 tabular-nums group-hover:text-brand-primary transition-colors duration-300">{stat.number}</div>
                  <div className="text-base font-bold text-gray-300">{stat.label}</div>
                  <div className="w-8 h-0.5 bg-brand-primary/10 mx-auto mt-6 group-hover:w-16 group-hover:bg-brand-primary transition-all duration-300 rounded-full"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="bg-brand-dark rounded-lg p-8 lg:p-12 text-center text-white relative overflow-hidden shadow-xl">
              <div className="absolute inset-0 bg-brand-primary opacity-10"></div>
              <div className="relative z-10 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-5xl font-extrabold mb-6 tracking-tight">Ready to <span className="text-brand-primary">Partner</span> With Us?</h2>
                <p className="text-gray-300 mb-8 text-base">Whether you are an individual leader or a corporate team, we have a pathway for you.</p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link href="/programs" className="px-10 py-4 bg-brand-primary text-white font-bold rounded-md hover:shadow-lg hover:shadow-brand-primary/30 transition-all active:scale-95 text-base">
                    Explore Programs
                  </Link>
                  <Link href="/contact" className="px-10 py-4 bg-transparent border-2 border-white/20 text-white font-bold rounded-md hover:bg-white/10 hover:border-white transition-all text-base">
                    Schedule Consultation
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
